// Import this once in the app to enable Angular extraction of $localize i18n strings in ts code
import '@angular/localize/init';
import { Component, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { debounceTime, filter } from 'rxjs/operators';
import { AutoSub, AutoSubOnDestroy, MapService, ProjectService, StatsService, UserProjectService, UserService, UserStateService } from '@motes-sdk/providers';
import { MotesLoginService } from '@motes-sdk/motes-login';
import { ProfileMediaService } from '@motes-sdk/providers/profile-media.service';
import { Mote, MotesPaths, ServiceId } from '@motes-sdk/models';
import { MapEditorService } from './services';
import { TourValidationService } from './services/tour-validation.service';
import { fromEvent } from 'rxjs';

@AutoSubOnDestroy()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss', '../../node_modules/mapbox-gl/dist/mapbox-gl.css'],
    encapsulation: ViewEncapsulation.None
})
export class AppComponent {

    public editorMode: boolean = false;
    public showMap: boolean = false;
    public isWindowTooNarrow: boolean = false;
    public isNarrowWindowIgnored: boolean = false;

    constructor(
        private projectService: ProjectService,
        private userProjectService: UserProjectService,
        private userStateService: UserStateService,
        private mapService: MapService,
        private mapEditorService: MapEditorService,
        private route: ActivatedRoute,
        public loginService: MotesLoginService,
        private router: Router,
        public userService: UserService,
        private profileMediaService: ProfileMediaService,
        private statsService: StatsService,
        private tourValidationService: TourValidationService,
    ) {
        this.init();
    }

    private init() {
        // Init project service
        this.projectService.init();
        // Init profile media service - make sure all new users get a profile picture
        this.profileMediaService.init(this.userService);
        // Init login service with UserFactory
        this.loginService.init(this.userService);
        // Init stat event service
        this.statsService.init(ServiceId.CREATOR);
        this.statsService.enableUserVisitStats();

        this.tourValidationService.init();

        // DISABLED: creator updates modal — see class-level REMOVAL PLAN.
        // AutoSub(this).reg['creatorUpdatesSubscriptionSub'] = this.userService.watchCurrentUser().subscribe((user) => {
        //     if(!user || user.isAnonymous) {
        //         this.showCreatorUpdatesSubscriptionModal = false;
        //         return;
        //     }
        //     if(!this.creatorUpdatesSubscriptionPromptEnabled) {
        //         this.showCreatorUpdatesSubscriptionModal = false;
        //         return;
        //     }
        //     if(this.hasCreatorUpdatesDecisionInSession) return;
        //
        //     const creatorUpdatesSubscription = user.emailListSubscriptions?.[this.CREATOR_UPDATES_SUBSCRIPTION_KEY];
        //     this.showCreatorUpdatesSubscriptionModal = !creatorUpdatesSubscription?.updatedOn;
        // });

        // Watch route to determine map visibility
        AutoSub(this).reg['urlSub'] = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((ev: NavigationEnd) => {
            // If no url path is specified, redirect
            if(this.route.snapshot.firstChild.url.length <= 0) return this.goToDashboard();
            // Use route to decide whether to show editor header and map
            this.showMap = this.route.snapshot.firstChild?.firstChild?.firstChild?.data?.showMap || false;
            this.editorMode = this.route.snapshot.firstChild?.data?.showEditorMenu || false;
            // Get project key from url
            const projectKey = this.route.snapshot.firstChild?.firstChild?.params?.projectKey;
            // Get tour key from url (dashboard or editor)
            const tourKeyDashboard = this.route.snapshot.firstChild?.firstChild?.firstChild?.params?.tourKey;
            const tourKeyEditor = this.route.snapshot.firstChild?.firstChild?.params.tourKey;
            const tourKey = tourKeyDashboard || tourKeyEditor || '';

            // Change active project / tour if it exists in route
            if(projectKey) return this.projectService.changeActiveProject(projectKey, tourKey, null, false);
        });

        // Redirect user to dashboard if activeProject is not longer part of the active project.
        // i.e. if the user is removed as a member of the project.
        AutoSub(this).reg['projectSub'] = this.userProjectService.watchUserProjects().subscribe(async ([user, projects]) => {
            const activeProject = await this.projectService.getActiveProject();
            const updatedActiveProject = projects.find(x => x.key == activeProject.key);
            // Do not redirect if user is super admin (let super admin inspect a users project)
            if(user?.isSuperAdmin) return;

            if(!updatedActiveProject) {
                this.userStateService.clearStoredActiveTour();
                this.goToDashboard();
            }
        });

        // Check is device is wide enough to show minimum content, otherwise show overlay
        if(typeof window === 'undefined') return;
        this.isWindowTooNarrow = window.innerWidth < 1280;
        AutoSub(this).reg['widowResizeReg'] = fromEvent(window, 'resize').pipe(debounceTime(200)).subscribe(() => {
            this.isWindowTooNarrow = window.innerWidth < 1280;
        });
    }

    ngAfterViewInit() {
        this.mapService.init();
        this.mapEditorService.init();
    }

    /**
     * Navigate to local storage project, first of user's projects or 'no worlds' dashboard page
     */
    public async goToDashboard() {
        let user = await this.loginService.getUserCurrentRights();
        if(!user) return;
        let latestProject = await this.projectService.getLatestUserProject(user.uid);
        if(latestProject) return this.router.navigate(['dashboard', latestProject]);
        return this.router.navigateByUrl('dashboard');
    }

    public goToHomeCreator() {
        if(typeof window == 'undefined') return;
        window.open(MotesPaths.MOTES_HOME_CREATOR('en'), '_blank');
    }

    public async changeActiveStory(newStory: Mote) {
        // Redirect to story pane
        this.router.navigate(['editor', newStory.project, newStory.parent, 'stories', newStory.key, 'story']);
    }

    // DISABLED: Handled in new app
    // public async setCreatorUpdatesSubscription(isSubscribed: boolean) {
    //     if(this.isUpdatingCreatorUpdatesSubscription) return;
    //     const user = this.userService.currentUser;
    //     if(!user) return;
    //
    //     this.isUpdatingCreatorUpdatesSubscription = true;
    //     try {
    //         if(!user.emailListSubscriptions) user.emailListSubscriptions = {};
    //         user.emailListSubscriptions[this.CREATOR_UPDATES_SUBSCRIPTION_KEY] = {
    //             isSubscribed,
    //             updatedOn: this.userService.getServerTimestamp()
    //         };
    //         await this.userService.updateUser(user);
    //         this.hasCreatorUpdatesDecisionInSession = true;
    //         this.showCreatorUpdatesSubscriptionModal = false;
    //     } finally {
    //         this.isUpdatingCreatorUpdatesSubscription = false;
    //     }
    // }

}
